import { useState } from 'react';

import type { Axis } from '@/api/types';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { FieldLabel } from '@/ui/FieldLabel';
import { Modal } from '@/ui/Modal';
import { AXIS_OPTIONS, DESCRIPTION_MAX, LABEL_MAX, WEIGHT_MAX, WEIGHT_MIN, fieldErrors, initialFields, type Draft, type FieldErrors, type Fields } from './dimensionsDraft';

const NO_ERRORS: FieldErrors = { label: null, description: null, weight: null };

function LabelField({ value, error, onChange }: { value: string; error: string | null; onChange: (value: string) => void }) {
  return (
    <div className="field">
      <label htmlFor="dim-label">Label</label>
      <input
        id="dim-label"
        type="text"
        maxLength={LABEL_MAX}
        value={value}
        autoFocus
        {...fieldA11y('dim-label', error)}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldError inputId="dim-label" message={error} />
    </div>
  );
}

function QuestionField({ value, error, onChange }: { value: string; error: string | null; onChange: (value: string) => void }) {
  return (
    <div className="field">
      <FieldLabel htmlFor="dim-question" id="dim-question-hint" label="Question for the AI" hint="Research asks the AI this and scores the idea 0–100 on it." />
      <textarea
        id="dim-question"
        rows={4}
        maxLength={DESCRIPTION_MAX}
        value={value}
        {...fieldA11y('dim-question', error, 'dim-question-hint dim-question-count')}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="dim-description-foot">
        <span id="dim-question-count" className="hint char-count" aria-live="polite">
          {value.length} / {DESCRIPTION_MAX}
        </span>
      </span>
      <FieldError inputId="dim-question" message={error} />
    </div>
  );
}

function WeightField({ value, error, onChange }: { value: number | ''; error: string | null; onChange: (value: number | '') => void }) {
  return (
    <div className="field">
      <FieldLabel htmlFor="dim-weight" id="dim-weight-hint" label="Weight" hint="How much it counts in the total and the verdict." />
      <div className="input-suffix">
        <input
          id="dim-weight"
          type="number"
          inputMode="numeric"
          step={1}
          min={WEIGHT_MIN}
          max={WEIGHT_MAX}
          value={value}
          {...fieldA11y('dim-weight', error, 'dim-weight-hint')}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
        <span className="suffix" aria-hidden="true">%</span>
      </div>
      <FieldError inputId="dim-weight" message={error} />
    </div>
  );
}

function AxisField({ value, onChange }: { value: Axis | null; onChange: (value: Axis | null) => void }) {
  return (
    <div className="field">
      <FieldLabel htmlFor="dim-axis" id="dim-axis-hint" label="Matrix axis" hint="Where it plots on the Impact × Feasibility matrix." />
      <select id="dim-axis" aria-describedby="dim-axis-hint" value={value ?? ''} onChange={(e) => onChange((e.target.value || null) as Axis | null)}>
        {AXIS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Add or edit one dimension of the draft. Nothing is saved until Save. */
export function DimensionDialog({
  initial,
  takenLabels,
  onApply,
  onClose,
}: {
  initial: Draft | null;
  takenLabels: string[];
  onApply: (fields: Fields) => void;
  onClose: () => void;
}) {
  const [start] = useState(() => initialFields(initial));
  const [label, setLabel] = useState(start.label);
  const [description, setDescription] = useState(start.description);
  const [weight, setWeight] = useState<number | ''>(start.weight);
  const [axis, setAxis] = useState<Axis | null>(start.axis);
  const [tried, setTried] = useState(false);

  const errors = fieldErrors(label, description, weight, takenLabels);
  const valid = Object.values(errors).every((e) => !e);
  // Errors show only once Add / Apply has been tried.
  const shown = tried ? errors : NO_ERRORS;

  return (
    <Modal
      title={initial ? `Edit ${initial.label}` : 'Add a dimension'}
      subtitle="Changes join the draft below; Save there commits them all, once the weights add up to 100%."
      onClose={onClose}
      onSubmit={() => {
        setTried(true);
        if (valid && weight !== '') onApply({ label: label.trim(), description: description.trim(), weight, axis });
      }}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            {initial ? 'Apply' : 'Add'}
          </button>
        </>
      }
    >
      <div className="stack-md">
        <LabelField value={label} error={shown.label} onChange={setLabel} />
        <QuestionField value={description} error={shown.description} onChange={setDescription} />
        <div className="grid grid-2">
          <WeightField value={weight} error={shown.weight} onChange={setWeight} />
          <AxisField value={axis} onChange={setAxis} />
        </div>
      </div>
    </Modal>
  );
}
